import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import styled from "styled-components/native"

import { joinContributorNames } from "@treefort/lib/contributor"
import { formatDate } from "@treefort/lib/date"
import { Result } from "@treefort/lib/result"
import icons from "@treefort/tokens/app/icons"

import { useActiveProfileId } from "../../hooks/use-active-profile-id"
import useAppManifest from "../../hooks/use-app-manifest"
import { useAsyncViewPropsForQueries } from "../../hooks/use-async-view-props-for-queries"
import useContent from "../../hooks/use-content"
import { useOpenCheckoutPage } from "../../hooks/use-open-checkout-page"
import {
  canDownloadConsumableContent,
  getConsumableContentFromPodcastResponse,
} from "../../lib/consumable-content"
import { playContentAudio } from "../../lib/content-audio"
import { createStyle } from "../../lib/create-style"
import { kebabCase } from "../../lib/kebab-case"
import { syncManager } from "../../watermelon/sync"
import AddToLibraryButton, { canAddToLibrary } from "../add-to-library-button"
import { AppLink } from "../app-link"
import { AsyncButton } from "../async-button"
import { useAsyncViewProps } from "../async-view"
import { BoxPadding } from "../box"
import DownloadButton from "../download-button"
import { Heading } from "../heading"
import Icon from "../icon"
import LockedContentButton from "../locked-content-button"
import { Markdown } from "../markdown"
import { MetadataSpacer, MetadataText } from "../metadata"
import ModuleArtworkLayout from "../module-artwork-layout"
import ProgressForConsumableContent from "../progress-for-consumable-content"
import Row from "../row"
import Spacer from "../spacer"
import Text from "../text"

const buttonFlexContainerStyle = createStyle({ flex: 1 })

const PlayButton = styled(AsyncButton)`
  flex: ${(props) =>
    props.theme.audiobookModule.playButtonFlex ? "1" : "none"};
`

const LockedPlayButton = styled(LockedContentButton)`
  flex: ${(props) =>
    props.theme.audiobookModule.playButtonFlex ? "1" : "none"};
`

export function PodcastEpisodeModule({
  podcastId,
  episodeNumber,
  maxWidth,
  paddingTop = "medium",
}: {
  podcastId: number
  episodeNumber: number
  maxWidth?: number
  paddingTop?: BoxPadding
}) {
  const profileId = useActiveProfileId()
  const podcast = useContent(podcastId, "podcast")
  const [artworkReady, setArtworkReady] = useState(false)
  const { t, i18n } = useTranslation()
  const podcastEpisode = podcast.data?.details.episodes.find(
    (episode) => episode.episode === episodeNumber,
  )

  const openCheckoutPage = useOpenCheckoutPage({
    availability: podcastEpisode?.audioMedia,
    contentId: podcastId,
  })

  useEffect(() => {
    syncManager.requestSync({ syncType: "user-initiated" })
  }, [])

  const consumableContent = Result.mapSuccess(podcast, (podcast) => {
    try {
      return Result.success(
        getConsumableContentFromPodcastResponse(podcast, episodeNumber),
      )
    } catch (error) {
      return Result.error(error)
    }
  })
  const showDownloadButton =
    consumableContent.isSuccess &&
    canDownloadConsumableContent(consumableContent.data)
  const isLocked = podcastEpisode?.audioMedia?.status === "notAvailable"
  const showAddToLibraryButton = canAddToLibrary({
    manifest: useAppManifest(),
    consumableContent: consumableContent.data,
  })
  const description = podcastEpisode?.description || podcast.data?.description

  const playPodcastEpisode = useCallback(() => {
    if (isLocked) {
      openCheckoutPage()
    } else if (!isLocked && consumableContent.isSuccess) {
      return playContentAudio({
        consumableContent: consumableContent.data,
        profileId,
      })
    }
  }, [consumableContent, isLocked, openCheckoutPage, profileId])

  const asyncViewPropsForQuery = useAsyncViewPropsForQueries(podcast, {
    forceLoading: podcast.data?.artworkMedia ? !artworkReady : false,
  })

  useAsyncViewProps(
    consumableContent.error instanceof Error &&
      consumableContent.error.message.includes("not found")
      ? {
          state: "error",
          title: t("Not Found"),
          message: t("Sorry, that page doesn't exist!"),
        }
      : asyncViewPropsForQuery,
  )

  const host = joinContributorNames(podcast.data?.contributors, "host")
  const publisher = joinContributorNames(
    podcast.data?.contributors,
    "publisher",
  )

  const shouldShowDate = Boolean(
    podcastEpisode?.date &&
      consumableContent.data?.content.dateDisplayStrategy !== "hidden",
  )

  return podcast.data && podcastEpisode ? (
    <ModuleArtworkLayout
      paddingTop={paddingTop}
      onArtworkReady={setArtworkReady}
      artwork={
        podcastEpisode.artworkMedia?.url ||
        podcast.data?.artworkMedia?.original.url
      }
      maxWidth={maxWidth}
    >
      <Heading level={1} textStyle="headingLarge" color="primary">
        {podcastEpisode.title}
      </Heading>
      <Spacer size="small" />
      <AppLink
        id={kebabCase(
          `navigate-to-parent-podcast-${podcast.data.id}-${podcast.data.title}`,
        )}
        to={{
          type: "content",
          contentId: podcast.data.id,
          contentType: "podcast",
        }}
        aria-label={podcast.data.title}
      >
        <Row>
          <Text textStyle="body" numberOfLines={1}>
            {podcast.data.title}
          </Text>
          <Spacer size="tiny" horizontal />
          <Icon source={icons.chevronRight} size="xsmall" />
        </Row>
      </AppLink>
      <Spacer size="small" />
      <Row>
        {host ? <MetadataText>{host}</MetadataText> : null}
        {host && shouldShowDate ? <MetadataSpacer /> : null}
        {shouldShowDate && podcastEpisode.date ? (
          <MetadataText>
            {formatDate(new Date(podcastEpisode.date), {
              strategy:
                consumableContent.data?.content.dateDisplayStrategy ||
                "naturalDate",
              i18n,
            })}
          </MetadataText>
        ) : null}
        <ProgressForConsumableContent
          flex={1}
          consumableContent={consumableContent.data}
          includeProgressLabel
          includeFinishedBadge
          childrenBefore={
            host || podcastEpisode.date ? <MetadataSpacer /> : null
          }
        />
      </Row>
      {publisher ? <MetadataText>{publisher}</MetadataText> : null}
      <ProgressForConsumableContent
        consumableContent={consumableContent.data}
        includeProgressBar
        childrenBefore={<Spacer size="small" />}
      />
      <Spacer size="medium" />
      <Row>
        {podcastEpisode.audioMedia?.status === "notAvailable" ? (
          <LockedPlayButton
            id={kebabCase(
              `podcast-episode-locked-${podcast.data.id}-${podcastEpisode.episode}-${podcastEpisode.title}`,
            )}
            openCheckoutPage={openCheckoutPage}
            availability={podcastEpisode.audioMedia}
            action="listen"
            containerStyle={buttonFlexContainerStyle}
          />
        ) : (
          <PlayButton
            id={kebabCase(
              `podcast-episode-play-${podcast.data.id}-${podcastEpisode.episode}-${podcastEpisode.title}`,
            )}
            icon={icons.headphones}
            type="primary"
            onPress={playPodcastEpisode}
            disabled={!podcast.isSuccess}
            containerStyle={buttonFlexContainerStyle}
          >
            {t("Listen")}
          </PlayButton>
        )}
        {showDownloadButton ? (
          <>
            <Spacer size={"large"} horizontal />
            <DownloadButton consumableContent={consumableContent.data} />
          </>
        ) : null}
        {showAddToLibraryButton ? (
          <>
            <Spacer size={showDownloadButton ? "small" : "large"} horizontal />
            <AddToLibraryButton consumableContent={consumableContent.data} />
          </>
        ) : null}
      </Row>
      {description ? (
        <>
          <Spacer size="medium" />
          <Markdown maxAllowedHeadingLevel={2}>{description}</Markdown>
        </>
      ) : null}
    </ModuleArtworkLayout>
  ) : null
}
